import { useState, useRef } from 'react';
import { api, Course } from '../lib/api';
import { X, Upload, CheckCircle, AlertCircle, Download } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  courses: Course[];
}

interface ParsedLesson {
  title: string;
  description?: string;
  content?: string;
  url: string;
  order_index?: number;
}

export default function CSVImportModal({ isOpen, onClose, onImportComplete, courses }: Props) {
  const [step, setStep] = useState<'upload' | 'preview' | 'importing' | 'complete'>('upload');
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [parsedLessons, setParsedLessons] = useState<ParsedLesson[]>([]);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ successful: number; failed: number; errors: string[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setStep('upload');
    setSelectedCourse('');
    setParsedLessons([]);
    setError('');
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const parseCSV = (content: string): ParsedLesson[] => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must have at least a header row and one data row');
    }

    // Parse header
    const header = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
    const titleIdx = header.findIndex(h => h === 'title' || h === 'name' || h === 'lesson');
    const urlIdx = header.findIndex(h => h === 'url' || h === 'link');
    const descIdx = header.findIndex(h => h === 'description' || h === 'desc');
    const contentIdx = header.findIndex(h => h === 'content' || h === 'body' || h === 'text');

    if (titleIdx === -1) {
      throw new Error('CSV must have a "title" column');
    }
    if (urlIdx === -1) {
      throw new Error('CSV must have a "url" column');
    }

    const lessons: ParsedLesson[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);

      const title = values[titleIdx]?.trim();
      const url = values[urlIdx]?.trim();

      if (!title || !url) {
        continue; // Skip empty rows
      }

      lessons.push({
        title,
        url,
        description: descIdx !== -1 ? values[descIdx]?.trim() : undefined,
        content: contentIdx !== -1 ? values[contentIdx]?.trim() : undefined,
        order_index: i - 1,
      });
    }

    return lessons;
  };

  // Parse a CSV line handling quoted values
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current);
    return result;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');

    try {
      const content = await file.text();
      const lessons = parseCSV(content);

      if (lessons.length === 0) {
        throw new Error('No valid lessons found in CSV');
      }

      setParsedLessons(lessons);
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse CSV');
    }
  };

  const handleImport = async () => {
    if (!selectedCourse) {
      setError('Please select a course');
      return;
    }

    setError('');
    setStep('importing');

    try {
      const result = await api.importLessonsFromCSV(selectedCourse, parsedLessons);
      setResult(result);
      setStep('complete');
      onImportComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setStep('preview');
    }
  };

  const downloadTemplate = () => {
    const template = 'title,url,description,content\n"Introduction to the Course","https://example.com/lesson-1","Welcome to our course","Full lesson content here"\n"Lesson 2: Getting Started","https://example.com/lesson-2","Getting started guide","More content..."';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lessons_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Import Lessons from CSV</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Step: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-4">Select a CSV file to import lessons</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Choose CSV File
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">CSV Format Requirements</h3>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>Required columns: <code className="bg-gray-200 px-1 rounded">title</code> and <code className="bg-gray-200 px-1 rounded">url</code></li>
                  <li>Optional columns: <code className="bg-gray-200 px-1 rounded">description</code>, <code className="bg-gray-200 px-1 rounded">content</code></li>
                  <li>First row must be the header</li>
                </ul>
                <button
                  onClick={downloadTemplate}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Download className="w-4 h-4" />
                  Download template CSV
                </button>
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {step === 'preview' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Course to Import Into
                </label>
                <select
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Choose a course...</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Preview ({parsedLessons.length} lessons found)
                </h3>
                <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Title</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">URL</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {parsedLessons.slice(0, 10).map((lesson, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                          <td className="px-3 py-2 text-gray-900 truncate max-w-[200px]">
                            {lesson.title}
                          </td>
                          <td className="px-3 py-2 text-gray-500 truncate max-w-[200px]">
                            {lesson.url}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsedLessons.length > 10 && (
                    <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50 text-center">
                      ... and {parsedLessons.length - 10} more lessons
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setStep('upload');
                    setParsedLessons([]);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={!selectedCourse}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Import {parsedLessons.length} Lessons
                </button>
              </div>
            </div>
          )}

          {/* Step: Importing */}
          {step === 'importing' && (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Importing {parsedLessons.length} lessons...</p>
            </div>
          )}

          {/* Step: Complete */}
          {step === 'complete' && result && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Import Complete</h3>
              <p className="text-gray-600 mb-4">
                Successfully imported {result.successful} of {result.successful + result.failed} lessons
              </p>

              {result.errors.length > 0 && (
                <div className="mt-4 text-left bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-800 mb-2">Errors:</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    {result.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                    {result.errors.length > 5 && (
                      <li className="text-red-500">... and {result.errors.length - 5} more errors</li>
                    )}
                  </ul>
                </div>
              )}

              <button
                onClick={handleClose}
                className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
